import { apiRequest, ApiRequestError } from '../lib/httpClient'
import type {
  DataTypeName,
  RuleConfig,
  RuleEntry,
  RuleSeverity,
  RuleSet,
  RuleType,
  SaveRuleSetInput,
  ViewGrant,
  ViewTemplate,
} from '../types/fileUploadRules'
import { sanitizeConfig } from '../features/fileUploadRules/ruleConfig'
import { asArray, pickNumber, pickString } from './shared/coercion'

const BASE_PATH = 'FileUploadRule/ui'

function pickBool(value: unknown, fallback = false): boolean {
  if (value == null) return fallback
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return Boolean(value)
}

function normalizeConfig(raw: unknown): RuleConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const source = raw as Record<string, unknown>
  const config: RuleConfig = {}

  const type = pickString(source['type'] ?? source['Type'])
  if (type) config.type = type as DataTypeName

  const min = source['min'] ?? source['Min']
  const max = source['max'] ?? source['Max']
  const minLength = source['minLength'] ?? source['MinLength']
  const maxLength = source['maxLength'] ?? source['MaxLength']
  if (min != null && min !== '') config.min = pickNumber(min)
  if (max != null && max !== '') config.max = pickNumber(max)
  if (minLength != null && minLength !== '') config.minLength = pickNumber(minLength)
  if (maxLength != null && maxLength !== '') config.maxLength = pickNumber(maxLength)

  const pattern = pickString(source['pattern'] ?? source['Pattern'])
  if (pattern) config.pattern = pattern
  if ('ignoreCase' in source || 'IgnoreCase' in source) {
    config.ignoreCase = pickBool(source['ignoreCase'] ?? source['IgnoreCase'])
  }

  const values = source['values'] ?? source['Values']
  if (Array.isArray(values)) config.values = values.map((item) => String(item))
  if ('caseInsensitive' in source || 'CaseInsensitive' in source) {
    config.caseInsensitive = pickBool(source['caseInsensitive'] ?? source['CaseInsensitive'])
  }
  if ('removeNullRows' in source || 'RemoveNullRows' in source) {
    config.removeNullRows = pickBool(source['removeNullRows'] ?? source['RemoveNullRows'])
  }

  return config
}

function normalizeRuleEntry(raw: Record<string, unknown>): RuleEntry {
  const ruleType = (pickString(raw['ruleType'] ?? raw['RuleType']) || 'RequiredNonNull') as RuleType
  return {
    columnName: pickString(raw['columnName'] ?? raw['ColumnName']),
    ruleType,
    severity: (pickString(raw['severity'] ?? raw['Severity']) || 'Error') as RuleSeverity,
    isActive: pickBool(raw['isActive'] ?? raw['IsActive'], true),
    config: normalizeConfig(raw['config'] ?? raw['Config']),
  }
}

function normalizeGrant(raw: unknown): ViewGrant {
  const source = (raw || {}) as Record<string, unknown>
  return {
    privilege: pickString(source['privilege'] ?? source['Privilege']),
    grantee: pickString(source['grantee'] ?? source['Grantee']),
  }
}

function normalizeRuleSet(raw: Record<string, unknown>): RuleSet {
  const hasViews = pickBool(raw['HasViews'] ?? raw['hasViews'], false)
  const grants = asArray<unknown>(raw['ViewGrants'] ?? raw['viewGrants']).map(normalizeGrant)
  return {
    id: pickString(raw['Id'] ?? raw['id']) || undefined,
    tableName: pickString(raw['TableName'] ?? raw['tableName']),
    isActive: pickBool(raw['IsActive'] ?? raw['isActive'], true),
    hasViews,
    viewName: hasViews ? pickString(raw['ViewName'] ?? raw['viewName']) || undefined : undefined,
    viewDefinition: hasViews ? pickString(raw['ViewDefinition'] ?? raw['viewDefinition']) || undefined : undefined,
    viewOwner: hasViews ? pickString(raw['ViewOwner'] ?? raw['viewOwner']) || undefined : undefined,
    viewGrants: hasViews && grants.length ? grants : undefined,
    rules: asArray<Record<string, unknown>>(raw['Rules'] ?? raw['rules']).map(normalizeRuleEntry),
    createdAt: pickString(raw['CreatedAt'] ?? raw['createdAt']) || undefined,
    updatedAt: pickString(raw['UpdatedAt'] ?? raw['updatedAt']) || undefined,
    createdBy: pickString(raw['CreatedBy'] ?? raw['createdBy']) || undefined,
  }
}

function toRequestEntry(entry: RuleEntry) {
  const config = sanitizeConfig(entry.ruleType, entry.config)
  // Entry keys are PascalCase; Config's inner keys stay lowercase (from sanitizeConfig).
  return {
    ColumnName: entry.columnName.trim(),
    RuleType: entry.ruleType,
    Severity: entry.severity,
    IsActive: entry.isActive,
    ...(config ? { Config: config } : {}),
  }
}

export async function fetchRuleSets(): Promise<RuleSet[]> {
  const payload = await apiRequest<unknown>(BASE_PATH, { method: 'GET' })
  const root = (payload || {}) as Record<string, unknown>
  const list = Array.isArray(payload)
    ? (payload as Record<string, unknown>[])
    : asArray<Record<string, unknown>>(root['items'] ?? root['Items'])
  return list.map(normalizeRuleSet)
}

export async function fetchRuleSet(tableName: string): Promise<RuleSet | null> {
  try {
    const payload = await apiRequest<Record<string, unknown>>(`${BASE_PATH}/${encodeURIComponent(tableName)}`, {
      method: 'GET',
    })
    if (!payload) return null
    return normalizeRuleSet(payload)
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) return null
    throw error
  }
}

export async function saveRuleSet(input: SaveRuleSetInput): Promise<RuleSet> {
  const payload = await apiRequest<Record<string, unknown>>(
    `${BASE_PATH}/${encodeURIComponent(input.tableName)}`,
    {
      method: 'PUT',
      body: {
        IsActive: input.isActive,
        HasViews: input.hasViews,
        // API stores null for all view fields when HasViews is false, regardless of what we send.
        ViewName: input.hasViews ? input.viewName?.trim() || null : null,
        ViewDefinition: input.hasViews ? input.viewDefinition?.trim() || null : null,
        ViewOwner: input.hasViews ? input.viewOwner?.trim() || null : null,
        ViewGrants: input.hasViews
          ? (input.viewGrants ?? [])
              .map((grant) => ({ privilege: grant.privilege.trim(), grantee: grant.grantee.trim() }))
              .filter((grant) => grant.privilege && grant.grantee)
          : null,
        Rules: input.rules.map(toRequestEntry),
      },
    },
  )
  // Some upsert responses echo the saved set; fall back to the sent values.
  if (payload && (payload['TableName'] || payload['tableName'] || payload['Rules'] || payload['rules'])) {
    return normalizeRuleSet(payload)
  }
  return {
    tableName: input.tableName,
    isActive: input.isActive,
    hasViews: input.hasViews,
    viewName: input.hasViews ? input.viewName : undefined,
    viewDefinition: input.hasViews ? input.viewDefinition : undefined,
    viewOwner: input.hasViews ? input.viewOwner : undefined,
    viewGrants: input.hasViews ? input.viewGrants : undefined,
    rules: input.rules,
  }
}

export async function deleteRuleSet(tableName: string): Promise<void> {
  await apiRequest(`${BASE_PATH}/${encodeURIComponent(tableName)}`, { method: 'DELETE' })
}

export async function fetchViewTemplates(): Promise<ViewTemplate[]> {
  const payload = await apiRequest<unknown>(`${BASE_PATH}/view-templates`, { method: 'GET' })
  const root = (payload || {}) as Record<string, unknown>
  const list = Array.isArray(payload)
    ? (payload as Record<string, unknown>[])
    : asArray<Record<string, unknown>>(root['items'] ?? root['Items'])
  return list.map((row) => ({
    key: pickString(row['Key'] ?? row['key']),
    name: pickString(row['Name'] ?? row['name']) || pickString(row['Key'] ?? row['key']),
  }))
}

// Pull the index-prefixed validation strings out of a 400 error payload.
export function extractRuleSetErrors(error: unknown): string[] {
  if (!(error instanceof ApiRequestError)) return []
  const payload = (error.payload || {}) as Record<string, unknown>
  return asArray<unknown>(payload['errors'] ?? payload['Errors']).map((item) => String(item))
}
